import { ECardType } from '__generated__/globalTypes';
import { ScrollHorizontal } from 'elements/functional/ScrollHorizontal';
import { EFonts } from 'libs/styles/styling';
import { getSmallerImage } from 'libs/utils/imageUtils/imageUtils';
import { consistentShuffleArray } from 'libs/utils/shuffle/consistentShuffle';
import { useAnalytics } from 'libs/utils/site/analytics';
import { useContext } from 'react';
import { useNavigate } from 'react-router';
import { CtxEventView } from 'session/ctxEventView';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    listId: string;
}

export const HomepagePromotionMobile = (props: Props): JSX.Element => {
    const ctxEventView = useContext(CtxEventView);
    const analytics = useAnalytics();
    const navigate = useNavigate();

    const entityDefinition = ctxEventView.otherEntityDefinitions.find((e) => e.id === props.listId);

    if (!entityDefinition) {
        return null;
    }

    const stories = entityDefinition?.list
        .map((e) => ctxEventView.allStories.find((s) => s.storyId === e.storyId))
        .map((e) => {
            if (!e) {
                return null;
            }
            //find the first image card
            for (const card of e.storyCards) {
                if (card.type === ECardType.IMAGE && card.__typename === 'ImageCard') {
                    const imageUrl = card.images[0].imageUrl;
                    const scaledImageUrl = getSmallerImage(imageUrl, 400);
                    return { storyId: e.storyId, imageUrl: scaledImageUrl };
                }
            }
            return null;
        })
        .filter(Boolean);

    const color = ctxEventView.eventData.eventInfo.styling.matchColor;

    if (!stories.length) {
        return null;
    }

    function onClick(storyId: string) {
        analytics.track('event_ui.detail.open_clicked', {
            type: 'promotion',
            storyId,
            title: ctxEventView.allStories.find((e) => e.storyId === storyId)?.title
        });
        const mobilePath = `/event/${ctxEventView.eventPath}/detail/${storyId}`;
        navigate(mobilePath);
    }

    const shuffledStories = consistentShuffleArray(stories, ctxEventView.shuffleHash);

    return (
        <S_HomepagePromotionMobile className={props.className}>
            <S_Title $color={color}>{entityDefinition.name}</S_Title>
            <S_Scroll>
                <S_Items $alignCenter={stories.length === 1}>
                    {shuffledStories.map((story) => {
                        return (
                            <S_Item
                                key={story.storyId}
                                onClick={() => {
                                    onClick(story.storyId);
                                }}
                            >
                                <S_Img $url={story.imageUrl} />
                            </S_Item>
                        );
                    })}
                </S_Items>
            </S_Scroll>
        </S_HomepagePromotionMobile>
    );
};

const S_HomepagePromotionMobile = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 15px;
    align-items: center;
`;

const S_Title = styled.div<{ $color: string }>`
    color: ${(props) => props.$color};
    font-size: 15px;
    font-family: ${EFonts.Medium};
`;

const S_Scroll = styled(ScrollHorizontal)`
    &::-webkit-scrollbar {
        display: none;
    }
`;

const S_Items = styled.div<{ $alignCenter: boolean }>`
    display: flex;
    flex-direction: row;
    gap: 15px;
    justify-content: ${(props) => (props.$alignCenter ? 'center' : 'flex-start')};
`;

const S_Item = styled.div`
    cursor: pointer;
`;

const S_Img = styled.div<{ $url: string }>`
    background-image: url(${(props) => props.$url});
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    width: 256px;
    height: 157px;
    border-radius: 27px;
`;
